import React, { useState, useRef, useEffect } from 'react';

const ImageCarousel = ({
  images = [],
  className = '',
  showDots = true,
  showArrows = true,
  autoHeight = true,
  onImageClick = null,
  accentColor = '#749555',
  layout = 'linkblock'
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [startX, setStartX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const carouselRef = useRef(null);

  const goToSlide = (index) => {
    if (isTransitioning || images.length === 0) return;

    setIsTransitioning(true);
    setCurrentIndex(index);

    setTimeout(() => setIsTransitioning(false), layout === 'tabs' ? 250 : 300);
  };

  const nextSlide = () => {
    // Circular navigation for all layouts
    const nextIndex = (currentIndex + 1) % images.length;
    goToSlide(nextIndex);
  };

  const prevSlide = () => {
    // Circular navigation for all layouts
    const prevIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    goToSlide(prevIndex);
  };

  // Touch/Mouse drag handlers
  const handleStart = (clientX) => {
    if (images.length <= 1) return;
    setIsDragging(true);
    setStartX(clientX);
    setDragOffset(0);
  };

  const handleMove = (clientX) => {
    if (!isDragging || images.length <= 1) return;

    const deltaX = clientX - startX;
    setDragOffset(deltaX);
  };

  const handleEnd = () => {
    if (!isDragging || images.length <= 1) return;

    setIsDragging(false);

    // Determine if we should switch slides based on drag distance
    const threshold = 50;
    const carouselWidth = carouselRef.current?.offsetWidth || 300;
    const dragPercentage = Math.abs(dragOffset) / carouselWidth;

    if (layout === 'tabs') {
      // Tab layout: simple threshold
      if (Math.abs(dragOffset) > threshold) {
        if (dragOffset > 0) {
          prevSlide();
        } else {
          nextSlide();
        }
      }
    } else {
      // Link block layout: threshold + percentage check
      if (Math.abs(dragOffset) > threshold && dragPercentage > 0.2) {
        if (dragOffset > 0) {
          prevSlide();
        } else {
          nextSlide();
        }
      }
    }

    setDragOffset(0);
  };

  // Mouse events
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    handleEnd();
  };

  // Touch events
  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (images.length <= 1) return;

      if (e.key === 'ArrowLeft') {
        prevSlide();
      } else if (e.key === 'ArrowRight') {
        nextSlide();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, images.length]);

  // Add global mouse move/up listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, startX]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!images || images.length === 0) {
    const bgColor = layout === 'tabs' ? 'bg-gray-800' : 'bg-gray-100';
    const textColor = layout === 'tabs' ? 'text-gray-400' : 'text-gray-500';
    return (
      <div className={`${bgColor} rounded-lg flex items-center justify-center h-64 ${className}`}>
        <div className="text-center">
          <i className="fas fa-images text-4xl text-gray-400 mb-4"></i>
          <p className={textColor}>No images to display</p>
        </div>
      </div>
    );
  }

  // Tab Layout: Modern Peek Carousel
  if (layout === 'tabs') {
    const hasNext = images.length >= 2; // Left peek (next) - show if 2+ items
    const hasPrev = images.length >= 3; // Right peek (prev) - show only if 3+ items

    // Mobile: Single centered photo only
    if (isMobile) {
      const transform = `translateX(${-currentIndex * 100 + (dragOffset / (carouselRef.current?.offsetWidth || 300)) * 100}%)`;

      return (
        <div className={`relative w-full ${className}`} ref={carouselRef}>
          <div className="flex justify-center">
            <div className="w-full max-w-lg">
              <div className="relative w-full rounded-xl overflow-hidden shadow-2xl">
                <div
                  className={`flex h-full transition-transform ${isTransitioning && !isDragging ? 'duration-300 ease-out' : 'duration-0'}`}
                  style={{ transform, aspectRatio: '4 / 3' }}
                  onMouseDown={handleMouseDown}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {images.map((image, index) => (
                    <div key={image.id || index} className="flex-none w-full h-full flex items-center justify-center">
                      <img
                        src={image.url}
                        alt={image.title || `Image ${index + 1}`}
                        className={`select-none max-w-full max-h-full object-contain rounded-xl ${onImageClick ? 'cursor-pointer' : ''}`}
                        loading="lazy"
                        onClick={() => onImageClick && onImageClick(image, index)}
                        draggable={false}
                        style={{ userSelect: 'none' }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Image Info */}
              {(images[currentIndex].title || images[currentIndex].description) && (
                <div className="mt-4 text-center">
                  {images[currentIndex].title && (
                    <h3 className="text-xl font-bold text-white mb-1">{images[currentIndex].title}</h3>
                  )}
                  {images[currentIndex].description && (
                    <p className="text-gray-400 text-sm">{images[currentIndex].description}</p>
                  )}
                </div>
              )}

              {/* Counter */}
              {images.length > 1 && (
                <div className="mt-2 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                  <i
                    className="fas fa-chevron-left text-xs cursor-pointer hover:text-gray-300 transition-colors"
                    onClick={prevSlide}
                  ></i>
                  <span>{currentIndex + 1} / {images.length}</span>
                  <i
                    className="fas fa-chevron-right text-xs cursor-pointer hover:text-gray-300 transition-colors"
                    onClick={nextSlide}
                  ></i>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Desktop: Peek preview layout with pre-rendered images
    const transform = `translateX(${-currentIndex * 100}%)`;
    const nextIndex = (currentIndex + 1) % images.length;
    const prevIndex = (currentIndex - 1 + images.length) % images.length;
    const nextPeekTransform = `translateX(${-nextIndex * 100}%)`;
    const prevPeekTransform = `translateX(${-prevIndex * 100}%)`;

    return (
      <div className={`relative w-full overflow-visible ${className}`} ref={carouselRef}>
        {/* Modern Peek Carousel */}
        <div className="relative flex items-center justify-center gap-4">
          {/* Prev Peek (on LEFT for flipped direction) - Pre-rendered */}
          {hasNext && (
            <div
              className="block w-1/4 flex-shrink-0 cursor-pointer transition-all duration-300 hover:scale-105"
              onClick={isTransitioning ? undefined : prevSlide}
            >
              <div className="relative w-full rounded-lg overflow-hidden opacity-50 hover:opacity-75">
                <div
                  className={`flex transition-transform ${isTransitioning ? 'duration-250 ease-out' : 'duration-0'}`}
                  style={{ transform: prevPeekTransform, aspectRatio: '4 / 3' }}
                >
                  {images.map((image, index) => (
                    <div key={image.id || index} className="flex-none w-full h-full flex items-center justify-center">
                      <img
                        src={image.url}
                        alt={image.title || `Image ${index + 1}`}
                        className="select-none w-full h-full object-cover rounded-lg"
                        loading="lazy"
                        draggable={false}
                        style={{ userSelect: 'none' }}
                      />
                    </div>
                  ))}
                </div>
                <div className="absolute inset-0 bg-black/40 pointer-events-none"></div>
              </div>
            </div>
          )}

          {/* Main Image - Pre-rendered carousel */}
          <div className="flex-1">
            <div className="relative w-full rounded-xl overflow-hidden shadow-2xl">
              <div
                className={`flex transition-transform ${isTransitioning ? 'duration-250 ease-out' : 'duration-0'}`}
                style={{ transform, aspectRatio: '4 / 3' }}
              >
                {images.map((image, index) => (
                  <div
                    key={image.id || index}
                    className="flex-none w-full h-full flex items-center justify-center"
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    <img
                      src={image.url}
                      alt={image.title || `Image ${index + 1}`}
                      className={`select-none max-w-full max-h-full object-contain rounded-xl ${onImageClick ? 'cursor-pointer' : ''}`}
                      loading="lazy"
                      onClick={() => onImageClick && onImageClick(image, index)}
                      draggable={false}
                      style={{ userSelect: 'none' }}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Image Info */}
            {(images[currentIndex].title || images[currentIndex].description) && (
              <div className="mt-4 text-center">
                {images[currentIndex].title && (
                  <h3 className="text-xl font-bold text-white mb-1">{images[currentIndex].title}</h3>
                )}
                {images[currentIndex].description && (
                  <p className="text-gray-400 text-sm">{images[currentIndex].description}</p>
                )}
              </div>
            )}

            {/* Counter */}
            {images.length > 1 && (
              <div className="mt-2 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                <i
                  className="fas fa-chevron-left text-xs cursor-pointer hover:text-gray-300 transition-colors"
                  onClick={prevSlide}
                ></i>
                <span>{currentIndex + 1} / {images.length}</span>
                <i
                  className="fas fa-chevron-right text-xs cursor-pointer hover:text-gray-300 transition-colors"
                  onClick={nextSlide}
                ></i>
              </div>
            )}
          </div>

          {/* Next Peek (on RIGHT for flipped direction) - Pre-rendered */}
          {hasPrev && (
            <div
              className="block w-1/4 flex-shrink-0 cursor-pointer transition-all duration-300 hover:scale-105"
              onClick={isTransitioning ? undefined : nextSlide}
            >
              <div className="relative w-full rounded-lg overflow-hidden opacity-50 hover:opacity-75">
                <div
                  className={`flex transition-transform ${isTransitioning ? 'duration-250 ease-out' : 'duration-0'}`}
                  style={{ transform: nextPeekTransform, aspectRatio: '4 / 3' }}
                >
                  {images.map((image, index) => (
                    <div key={image.id || index} className="flex-none w-full h-full flex items-center justify-center">
                      <img
                        src={image.url}
                        alt={image.title || `Image ${index + 1}`}
                        className="select-none w-full h-full object-cover rounded-lg"
                        loading="lazy"
                        draggable={false}
                        style={{ userSelect: 'none' }}
                      />
                    </div>
                  ))}
                </div>
                <div className="absolute inset-0 bg-black/40 pointer-events-none"></div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Navigation Arrows */}
        <div className="hidden">
          <button
            onClick={prevSlide}
            disabled={!hasPrev}
            className="w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center"
            aria-label="Previous image"
          >
            <i className="fas fa-chevron-left text-white"></i>
          </button>
          <button
            onClick={nextSlide}
            disabled={!hasNext}
            className="w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center justify-center"
            aria-label="Next image"
          >
            <i className="fas fa-chevron-right text-white"></i>
          </button>
        </div>
      </div>
    );
  }

  // Link Block Layout: Original Polaroid Frame Design
  const currentImage = images[currentIndex];
  const transform = `translateX(${-currentIndex * 100 + (dragOffset / (carouselRef.current?.offsetWidth || 300)) * 100}%)`;

  return (
    <div className={`relative ${className}`} ref={carouselRef}>
      {/* Image Counter - Top right of whole container */}
      {images.length > 1 && (
        <div className="absolute top-2 right-2 bg-chess-cream text-gray-800 px-3 py-1.5 text-sm font-bold shadow-lg border-2 border-black z-10">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Polaroid Container - Full width with parent padding */}
      <div className="relative w-full mx-auto max-w-2xl" >
        {/* Polaroid Frame - Theme colored background and border */}
        <div className="p-4 shadow-2xl rounded-lg border-4" style={{ background: `linear-gradient(to bottom, ${accentColor} 0%, #1a1a1a 100%)`, borderColor: accentColor, boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)' }}>

          {/* Rectangular Black Image Container - Shorter height */}
          <div className="relative w-full bg-black overflow-hidden" style={{ aspectRatio: '16 / 9' }}>
            <div
              className={`flex h-full transition-transform ${isTransitioning && !isDragging ? 'duration-300 ease-out' : 'duration-0'}`}
              style={{ transform }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}

            >
              {images.map((image, index) => (
                <div key={image.id || index} className="flex-none  w-full h-full flex items-center justify-center bg-black">
                  <img
                    src={image.url}
                    alt={image.title || `Image ${index + 1}`}
                    className={`select-none max-w-full max-h-full object-contain ${onImageClick ? 'cursor-pointer' : ''}`}
                    loading="lazy"
                    onClick={() => onImageClick && onImageClick(image, index)}
                    draggable={false}
                    style={{ userSelect: 'none' }}
                  />
                </div>
              ))}
            </div>

            {/* Dots Indicator */}
            {showDots && images.length > 1 && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-2">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      index === currentIndex
                        ? 'bg-gray-200 scale-125'
                        : 'bg-gray-500 hover:bg-gray-400'
                    }`}
                    disabled={isTransitioning}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Polaroid Caption Area with Navigation Arrows */}
          <div className="mt-4 min-h-[60px] p-4 flex items-center justify-between">
            {/* Left Arrow */}
            {showArrows && images.length > 1 && (
              <button
                onClick={prevSlide}
                className="bg-chess-cream hover:bg-chess-cream/80 text-black rounded-full w-12 h-12 flex items-center justify-center transition-all shadow-md hover:scale-110 flex-shrink-0"
                disabled={isTransitioning}
                aria-label="Previous image"
              >
                <i className="fas fa-chevron-left text-xl"></i>
              </button>
            )}

            {/* Caption Content - Center */}
            <div className="flex-1 px-4" >
              {images[currentIndex] && (images[currentIndex].title || images[currentIndex].description) ? (
                <div className="text-center space-y-1">
                  {images[currentIndex].title && (
                    <h3 className="font-bold text-xl text-chess-green">{images[currentIndex].title}</h3>
                  )}
                  {images[currentIndex].description && (
                    <p className="text-chess-green text-sm">{images[currentIndex].description}</p>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-chess-green text-sm italic">Photo {currentIndex + 1}</p>
                </div>
              )}
            </div>

            {/* Right Arrow */}
            {showArrows && images.length > 1 && (
              <button
                onClick={nextSlide}
                className="bg-chess-cream hover:bg-chess-cream/80 text-black rounded-full w-12 h-12 flex items-center justify-center transition-all shadow-md hover:scale-110 flex-shrink-0"
                disabled={isTransitioning}
                aria-label="Next image"
              >
                <i className="fas fa-chevron-right text-xl"></i>
              </button>
            )}
          </div>
        </div>

        {/* Drag indicator */}
        {isDragging && (
          <div className="absolute inset-0 bg-black/10 pointer-events-none" />
        )}
      </div>
    </div>
  );
};

export default ImageCarousel;
